import type { RecurrenceAnchorValue } from './deadline.constants';
import type { DeadlineRecurrenceChoice } from './deadline-management.constants';

/** Spec §3.8: sotto questa confidenza il campo arriva vuoto alla UI. */
export const DEADLINE_AI_CONFIDENCE_THRESHOLD = 0.6;

/** Perché la lettura non è riuscita: finisce nel log, la UI mostra solo `FAILED`. */
export const DEADLINE_AI_FAILURE_CODES = [
  'unreadable_file',
  'ai_no_output',
  'extraction_failed',
] as const;
export type DeadlineAiFailureCode = (typeof DEADLINE_AI_FAILURE_CODES)[number];

/** Valore letto dal modello con la sua confidenza 0..1 (0 quando `value` è null). */
export interface DeadlineAiReading {
  value: string | null;
  confidence: number;
}

/** Lettura grezza del modello, soglia esclusa: la soglia si applica quando la proposta riempie un campo. */
export interface DeadlineAiProposal {
  kind: 'PROPOSAL';
  schemaVersion: 1;
  promptVersion: string;
  documentTypeSlug: DeadlineAiReading;
  issuedAt: DeadlineAiReading;
  /** Scadenza stampata sul documento, se presente. */
  expiresAt: DeadlineAiReading;
}

/** Termini di catalogo che servono a proporre ricorrenza e scadenza. */
export interface DeadlineAiTypeTerms {
  id: string;
  name: string;
  defaultValidityMonths: number | null;
  warningOnly: boolean;
  recurrenceAnchor: RecurrenceAnchorValue | null;
  /** Serve a `resolveRecurrencePreselection` (tranche 9): solo avvisi = nessuna preselezione. */
  defaultWarningDays: readonly number[];
  /**
   * "Altro" si riconosce con `isOtherDocumentType(type)` (`catalogKey === 'altro'`), mai dallo slug.
   * Con questi campi `DeadlineAiTypeTerms` soddisfa `RecurrencePreselectionSource` della tranche 9.
   */
  catalogKey: string | null;
}

/** Riga a cui il documento appartiene già (ciclo corrente). */
export interface LinkedDeadlineTerms {
  documentTypeId: string | null;
  recurrence: DeadlineRecurrenceChoice;
  validityMonths: number | null;
  recurrenceAnchor: RecurrenceAnchorValue | null;
  /** `previousDueAt` del ciclo del documento (rinnovo), `YYYY-MM-DD`. */
  previousDueAt: string | null;
}

export type DeadlineAiTypeSource = 'AI' | 'DOCUMENT' | 'DEADLINE';
export type DeadlineAiDueAtSource = 'COMPUTED' | 'READ';

/** Proposta già filtrata dalla soglia e risolta con le regole di ricorrenza. */
export interface DeadlineAiSuggestionDto {
  documentTypeId: string | null;
  typeSource: DeadlineAiTypeSource | null;
  /** Nome del tipo letto dall'IA quando diverge dal tipo già scelto: "hai caricato il file giusto?". */
  typeMismatchName: string | null;
  recurrence: DeadlineRecurrenceChoice | null;
  issuedAt: string | null;
  validityMonths: number | null;
  recurrenceAnchor: RecurrenceAnchorValue | null;
  previousDueAt: string | null;
  dueAt: string | null;
  dueAtSource: DeadlineAiDueAtSource | null;
}

/**
 * Esito della lettura in anteprima: il file è comunque in deposito temporaneo (`uploadToken`).
 * `NOT_READ` è la risposta del caricamento senza lettura (`upload-staging`): nessuna quota, nessun modello.
 */
export const DEADLINE_AI_PREVIEW_STATUSES = [
  'READ',
  'FAILED',
  'QUOTA_EXHAUSTED',
  'UNAVAILABLE',
  'NOT_READ',
] as const;
export type DeadlineAiPreviewStatus = (typeof DEADLINE_AI_PREVIEW_STATUSES)[number];

export interface DeadlineAiPreviewDto {
  /** Monouso, legato a tenant e utente: sostituisce il file nel salvataggio del dialog. */
  uploadToken: string;
  status: DeadlineAiPreviewStatus;
  /** Presente solo con `status = READ`. */
  proposal: DeadlineAiProposal | null;
}

/** Solo se la lettura automatica della settimana è finita: quante ne restano non si mostra. */
export interface DeadlineAiQuotaDto {
  exhausted: boolean;
}

/** Il frontend smette di attendere la lettura dopo questo tempo e prosegue a mano. */
export const DEADLINE_AI_PREVIEW_TIMEOUT_MS = 45_000;

/** Oltre questo tempo il file in deposito temporaneo non è più utilizzabile e viene eliminato. */
export const DEADLINE_UPLOAD_TOKEN_TTL_HOURS = 24;

export const DEADLINE_UPLOAD_TOKEN_MAX_LENGTH = 100;
